import React, { useState, useEffect } from 'react';
import { XIcon, UsersIcon, CheckIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Modal for connecting band lineups to sheets
 * Allows users to select which band/lineup to connect to the current sheet
 */
const LineupConnectionModal = ({ isOpen, onClose, onConnect, sheetId }) => {
  const { user, isAuthenticated } = useAuth();
  const [userBands, setUserBands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);

  // Fetch user's bands when modal opens and user is authenticated
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      fetchUserBands();
    }
  }, [isOpen, isAuthenticated]);

  const fetchUserBands = async () => {
    console.log('LineupConnectionModal - fetchUserBands called, isAuthenticated:', isAuthenticated);
    setLoading(true);
    try {
      const response = await fetch('/api/bands?category=my', {
        credentials: 'include'
      });

      console.log('LineupConnectionModal - bands response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('LineupConnectionModal - bands result:', result);
        setUserBands(result.data || []);
      } else {
        console.error('Failed to fetch user bands, status:', response.status);
        setUserBands([]);
      }
    } catch (error) {
      console.error('Error fetching user bands:', error);
      setUserBands([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedConnection) return;

    try {
      // For now, we'll just call the onConnect callback with the selection
      // TODO: Replace with actual API call when backend is ready
      await onConnect(selectedConnection);
      onClose();
      setSelectedConnection(null);
    } catch (error) {
      console.error('Error connecting lineup:', error);
      alert('Failed to connect lineup. Please try again.');
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedConnection(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Connect Lineup to Sheet</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-4">
            Select a band or specific lineup to connect to this sheet:
          </p>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading your bands...</p>
            </div>
          ) : userBands.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">You're not a member of any bands yet.</p>
              <p className="text-sm text-gray-400 mt-2">
                Join or create a band to connect lineups to sheets.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {userBands.map(band => (
                <div key={band._id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <UsersIcon className="h-5 w-5 mr-2 text-gray-600" />
                    <h4 className="font-medium text-gray-900">{band.name}</h4>
                  </div>
                  
                  {/* Option to connect just the band */}
                  <div className="mb-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="connection"
                        value={`band-${band._id}`}
                        checked={selectedConnection?.type === 'band' && selectedConnection?.bandId === (band._id)}
                        onChange={() => setSelectedConnection({
                          type: 'band',
                          bandId: band._id,
                          bandName: band.name,
                          lineupId: 'band', // Use 'band' as a special lineupId for band-level connections
                          lineupName: null
                        })}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Use band: {band.name}
                      </span>
                    </label>
                  </div>

                  {/* Options for specific lineups */}
                  {band.lineups && band.lineups.length > 0 && (
                    <div className="ml-4 space-y-2">
                      <p className="text-xs text-gray-500 font-medium">Or choose a specific lineup:</p>
                      {band.lineups.map(lineup => (
                        <label key={lineup._id} className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="connection"
                            value={`lineup-${lineup._id}`}
                            checked={selectedConnection?.type === 'lineup' && selectedConnection?.lineupId === lineup._id}
                            onChange={() => setSelectedConnection({
                              type: 'lineup',
                              bandId: band._id,
                              bandName: band.name,
                              lineupId: lineup._id,
                              lineupName: lineup.name
                            })}
                            className="mr-2"
                          />
                          <div className="flex-1">
                            <span className="text-sm text-gray-700">{lineup.name}</span>
                            {lineup.members && lineup.members.length > 0 && (
                              <span className="text-xs text-gray-400 ml-2">
                                ({lineup.members.length} members)
                              </span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConnect}
            disabled={!selectedConnection}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedConnection
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <CheckIcon className="w-4 h-4 mr-1 inline" />
            Connect {selectedConnection?.type === 'lineup' ? 'Lineup' : 'Band'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LineupConnectionModal;